// Утилиты для парсинга и анализа данных таймлайна цеха
import { calculateWorkingHours, formatWorkingHours } from './workingHoursCalculator';

/**
 * Парсинг таймлайна цеха из текстового поля
 * @param {string|Array} timelineText - текст таймлайна или массив строк
 * @returns {Array} массив событий таймлайна
 */
export const parseTimeline = (timelineText) => {
  if (!timelineText) {
    return [];
  }

  const events = [];

  // Если это массив - используем его напрямую
  // Если строка - разбиваем на строки
  let lines = [];
  if (Array.isArray(timelineText)) {
    lines = timelineText.filter(line => line && line.trim());
  } else if (typeof timelineText === 'string') {
    lines = timelineText.split('\n').filter(line => line.trim());
  } else {
    return [];
  }

  lines.forEach(line => {
    // Парсим строку формата: "25.09.2025 10:18:00 / ЦЕХ/Резка стекла"
    // или "01.10.2025 12:08:27 / Готовые заказы/Готово к доставке"
    const match = line.match(/^(\d{2}\.\d{2}\.\d{4})\s+(\d{2}:\d{2}:\d{2})\s+\/\s+(.+)$/);

    if (match) {
      const [, date, time, stage] = match;

      // Конвертируем дату в ISO формат
      const [day, month, year] = date.split('.');
      const isoDateTime = `${year}-${month}-${day}T${time}`;

      // Определяем категорию (ЦЕХ, Готовые заказы и т.д.)
      const category = extractCategory(stage);

      events.push({
        timestamp: new Date(isoDateTime),
        dateStr: date,
        timeStr: time,
        stage: stage.trim(),
        stageName: extractStageName(stage),
        category: category,
        isProduction: category === 'ЦЕХ'
      });
    }
  });

  // Сортируем по времени
  events.sort((a, b) => a.timestamp - b.timestamp);

  return events;
};

/**
 * Извлечение категории из стадии (ЦЕХ, Готовые заказы и т.д.)
 */
const extractCategory = (stage) => {
  const match = stage.match(/^([^/]+)\//);
  return match ? match[1].trim() : 'Без категории';
};

/**
 * Извлечение названия стадии без префикса категории
 */
const extractStageName = (stage) => {
  // Удаляем префикс категории (например "ЦЕХ/" или "Готовые заказы/")
  return stage.replace(/^[^/]+\//, '').trim();
};

/**
 * Расчет длительности нахождения на каждой стадии
 * @param {Array} events - массив событий таймлайна
 * @returns {Array} массив с длительностями
 */
export const calculateStageDurations = (events) => {
  if (!events || events.length === 0) {
    return [];
  }

  const durations = [];

  for (let i = 0; i < events.length - 1; i++) {
    const currentEvent = events[i];
    const nextEvent = events[i + 1];

    // Календарное время
    const durationMs = nextEvent.timestamp - currentEvent.timestamp;
    const durationMinutes = Math.floor(durationMs / (1000 * 60));
    const calendarHours = (durationMs / (1000 * 60 * 60)).toFixed(2);
    const durationDays = (durationMs / (1000 * 60 * 60 * 24)).toFixed(2);

    // Рабочее время (с учетом рабочих часов и выходных)
    const workingHours = calculateWorkingHours(currentEvent.timestamp, nextEvent.timestamp);

    durations.push({
      stage: currentEvent.stageName,
      stageId: currentEvent.stage,
      startTime: currentEvent.timestamp,
      endTime: nextEvent.timestamp,
      durationMs,
      durationMinutes,
      durationHours: workingHours, // ИСПОЛЬЗУЕМ РАБОЧИЕ ЧАСЫ вместо календарных
      calendarHours: parseFloat(calendarHours), // Сохраняем календарные отдельно
      durationDays: parseFloat(durationDays),
      durationFormatted: formatWorkingHours(workingHours) // Форматируем рабочие часы
    });
  }

  // Добавляем последнюю стадию (если заказ еще не завершен)
  if (events.length > 0) {
    const lastEvent = events[events.length - 1];
    const now = new Date();
    const durationMs = now - lastEvent.timestamp;
    const durationMinutes = Math.floor(durationMs / (1000 * 60));
    const calendarHours = (durationMs / (1000 * 60 * 60)).toFixed(2);
    const durationDays = (durationMs / (1000 * 60 * 60 * 24)).toFixed(2);

    // Рабочее время
    const workingHours = calculateWorkingHours(lastEvent.timestamp, now);

    durations.push({
      stage: lastEvent.stageName,
      stageId: lastEvent.stage,
      startTime: lastEvent.timestamp,
      endTime: now,
      durationMs,
      durationMinutes,
      durationHours: workingHours, // ИСПОЛЬЗУЕМ РАБОЧИЕ ЧАСЫ
      calendarHours: parseFloat(calendarHours),
      durationDays: parseFloat(durationDays),
      durationFormatted: formatWorkingHours(workingHours),
      isCurrentStage: true
    });
  }

  return durations;
};

/**
 * Форматирование длительности в человекочитаемый вид
 */
const formatDuration = (ms) => {
  const seconds = Math.floor(ms / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) {
    const remainingHours = hours % 24;
    return `${days}д ${remainingHours}ч`;
  } else if (hours > 0) {
    const remainingMinutes = minutes % 60;
    return `${hours}ч ${remainingMinutes}м`;
  } else if (minutes > 0) {
    return `${minutes}м`;
  } else {
    return `${seconds}с`;
  }
};

/**
 * Получение текущей стадии заказа
 * @param {string|Array} timelineText - текст таймлайна или массив строк
 */
export const getCurrentStage = (timelineText) => {
  const events = parseTimeline(timelineText);
  if (events.length === 0) return null;

  const lastEvent = events[events.length - 1];
  return {
    stage: lastEvent.stageName,
    timestamp: lastEvent.timestamp,
    dateStr: lastEvent.dateStr,
    timeStr: lastEvent.timeStr
  };
};

/**
 * Получение общего времени прохождения всех стадий
 * @param {string|Array} timelineText - текст таймлайна или массив строк
 */
export const getTotalDuration = (timelineText) => {
  const events = parseTimeline(timelineText);
  if (events.length === 0) return null;

  const firstEvent = events[0];
  const lastEvent = events[events.length - 1];

  // Календарное время
  const durationMs = lastEvent.timestamp - firstEvent.timestamp;
  const calendarHours = (durationMs / (1000 * 60 * 60)).toFixed(2);

  // Рабочее время (с учетом рабочих часов и выходных)
  const workingHours = calculateWorkingHours(firstEvent.timestamp, lastEvent.timestamp);

  return {
    durationMs,
    durationMinutes: Math.floor(durationMs / (1000 * 60)),
    durationHours: workingHours, // ИСПОЛЬЗУЕМ РАБОЧИЕ ЧАСЫ
    calendarHours: parseFloat(calendarHours), // Сохраняем календарные отдельно
    durationDays: (durationMs / (1000 * 60 * 60 * 24)).toFixed(2),
    durationFormatted: formatWorkingHours(workingHours), // Форматируем рабочие часы
    startTime: firstEvent.timestamp,
    endTime: lastEvent.timestamp
  };
};

/**
 * Получение времени на конкретной стадии
 * @param {string|Array} timelineText - текст таймлайна или массив строк
 */
export const getStageTime = (timelineText, stageName) => {
  const durations = calculateStageDurations(parseTimeline(timelineText));

  const stageDuration = durations.find(d =>
    d.stage.toLowerCase().includes(stageName.toLowerCase())
  );

  return stageDuration || null;
};

/**
 * Расширенная обработка записи с добавлением временных метрик
 */
export const enrichItemWithTimelineData = (item) => {
  const timelineField = item.ufCrm3_1757404689894; // Поле "Таймлайн цеха"

  if (!timelineField) {
    return item;
  }

  const events = parseTimeline(timelineField);
  const durations = calculateStageDurations(events);
  const currentStage = getCurrentStage(timelineField);
  const totalDuration = getTotalDuration(timelineField);

  return {
    ...item,
    // Добавляем обработанные данные таймлайна
    _timeline: {
      events,
      durations,
      currentStage,
      totalDuration,
      stageCount: events.length
    },
    // Добавляем метрики для каждой стадии
    _timeOnRezka: getStageTime(timelineField, 'Резка')?.durationHours || 0,
    _timeOnObrabotka: getStageTime(timelineField, 'Обработка')?.durationHours || 0,
    _timeOnZakalka: getStageTime(timelineField, 'Закалка')?.durationHours || 0,
    _timeOnMatovka: getStageTime(timelineField, 'Матовка')?.durationHours || 0,
    _timeOnLoft: getStageTime(timelineField, 'Сборка')?.durationHours || 0,
    _timeOnUpak: getStageTime(timelineField, 'Упаковка')?.durationHours || 0,
    _timeOnKomplekt: getStageTime(timelineField, 'Комплектация')?.durationHours || 0,
    _totalProductionTime: totalDuration?.durationHours || 0,
    _currentStageName: currentStage?.stage || 'Неизвестно'
  };
};

/**
 * Обработка массива записей с добавлением временных данных
 */
export const enrichDataWithTimeline = (data) => {
  if (!Array.isArray(data)) return data;

  return data.map(item => enrichItemWithTimelineData(item));
};

/**
 * Статистика по стадиям для массива заказов
 */
export const getStageStatistics = (data) => {
  const enrichedData = enrichDataWithTimeline(data);

  const stats = {
    totalOrders: enrichedData.length,
    stages: {}
  };

  // Собираем статистику по каждой стадии
  enrichedData.forEach(item => {
    if (!item._timeline || !item._timeline.durations) return;

    item._timeline.durations.forEach(duration => {
      const stage = duration.stage;

      if (!stats.stages[stage]) {
        stats.stages[stage] = {
          name: stage,
          count: 0,
          totalHours: 0,
          avgHours: 0,
          minHours: Infinity,
          maxHours: 0,
          orders: []
        };
      }

      const stageStats = stats.stages[stage];
      stageStats.count++;
      stageStats.totalHours += duration.durationHours;
      stageStats.minHours = Math.min(stageStats.minHours, duration.durationHours);
      stageStats.maxHours = Math.max(stageStats.maxHours, duration.durationHours);
      stageStats.orders.push({
        orderId: item.id,
        orderNumber: item.ufCrm3_1746789101,
        durationHours: duration.durationHours
      });
    });
  });

  // Вычисляем средние значения
  Object.values(stats.stages).forEach(stage => {
    stage.avgHours = (stage.totalHours / stage.count).toFixed(2);
  });

  return stats;
};